import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Transaction } from '@solana/web3.js';
import { Buffer } from 'buffer';
import { useWallet } from '@solana/wallet-adapter-react';
import { useNetwork } from '../../utils/network';
import { useWalletReady } from '../../utils/useWalletReady';

/* THE ENTRY FEE, over the arena (tcg-game-server/docs/tournament-fees-and-prizes.md).
 *
 * The arena iframe has no network of its own, so when the game server answers a join with
 * FEE_REQUIRED the host (ArenaGamePage) opens this. It asks the member API for the quote
 * (the INC an AUD fee comes to right now), lets the member pick how to pay — their earned
 * INC, their wallet INC, or an external wallet — and pays exactly as the marketplace does:
 * earned INC in one call; the wallet modes as intent → the wallet signs → confirm. On a
 * paid answer the host sends the join the arena asked for.
 *
 * Nothing here decides the amount: the quote is the server's, shown as it came.
 *
 * It is drawn in the arena's own language (owner 2026-10-31) — the call popup's gold-edged
 * sheet, mono eyebrows, the cup and the amount as a plate, three pay rows with the balance on
 * its own line, the row you can pay with lit and the others dimmed with the reason — so it
 * reads as part of the game rather than a portal dialog over it. The arena's font stacks
 * are repeated here (it loads no webfont of its own, so the same stack renders the same). */
type Quote = {
  tournamentId: string;
  name: string;
  status: string;
  fee: {
    kind: 'inc' | 'aud';
    amount: number;
    audCents: number | null;
    incUnits: string;
    inc: string;
    priceUsd: number | null;
    audPerUsd: number | null;
  } | null;
  paid: boolean;
  balances: { earned: string; wallet: string; hasCustodyWallet: boolean };
};
type Mode = 'earnedTokens' | 'custody_inc' | 'external_inc';

const MONO = "'Geist Mono','JetBrains Mono',ui-monospace,SFMono-Regular,Menlo,monospace";
const SANS = "'Geist','Inter',system-ui,-apple-system,'Segoe UI',Roboto,sans-serif";
const GOLD = '#ffc44d';
const CYAN = '#38e8ff';
const TEXT = '#eaf6fa';
const DIM = '#8ea0b2';
const RED = '#ff6b7d';

const num = (s: string | number | null | undefined) => {
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};
const inc = (s: string | number | null | undefined) => {
  const n = num(s);
  return Number.isFinite(n) ? n.toLocaleString('en-AU', { maximumFractionDigits: 2 }) : String(s ?? '');
};
const words = (e: unknown, fallback: string) => {
  const err = e as { data?: { message?: string; code?: string }; message?: string } | null;
  return err?.data?.message || err?.message || fallback;
};

const eyebrow = { fontFamily: MONO, fontWeight: 800, fontSize: 11, letterSpacing: '0.2em', color: GOLD } as const;
const note = { fontFamily: MONO, fontWeight: 600, fontSize: 10.5, letterSpacing: '0.08em', lineHeight: 1.5, color: DIM } as const;

/* one way to pay: a row that is lit (you can), dimmed with the reason (you cannot), or busy */
const PayRow: React.FC<{
  label: string;
  sub: string;
  accent: 'gold' | 'cyan';
  reason?: string | null;
  busy?: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ label, sub, accent, reason, busy, disabled, onClick }) => {
  const off = !!disabled || !!reason;
  const color = accent === 'gold' ? GOLD : CYAN;
  const rgb = accent === 'gold' ? '255,196,77' : '56,232,255';
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={off || busy}
      aria-busy={busy || undefined}
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr) auto',
        alignItems: 'center',
        columnGap: 1.5,
        rowGap: 0.5,
        textAlign: 'left',
        p: '11px 14px',
        borderRadius: '12px',
        border: '1.5px solid',
        borderColor: off ? 'rgba(255,255,255,0.12)' : `rgba(${rgb},${busy ? 0.95 : 0.6})`,
        background: off ? 'rgba(255,255,255,0.03)' : `rgba(${rgb},${busy ? 0.18 : 0.09})`,
        color: off ? DIM : color,
        cursor: off ? 'not-allowed' : busy ? 'progress' : 'pointer',
        transition: 'background .15s, border-color .15s, transform .12s',
        '&:not(:disabled):hover': { background: `rgba(${rgb},0.18)`, borderColor: `rgba(${rgb},0.9)` },
        '&:not(:disabled):active': { transform: 'translateY(1px)' },
        '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: 2 }
      }}
    >
      <Box sx={{ fontFamily: SANS, fontWeight: 800, fontSize: 13.5, letterSpacing: '0.06em', textTransform: 'uppercase', lineHeight: 1.2, minWidth: 0 }}>
        {label}
      </Box>
      <Box sx={{ ...eyebrow, color: off ? DIM : color, fontSize: 10.5, letterSpacing: '0.14em', display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
        {busy ? <CircularProgress size={13} thickness={5} sx={{ color }} /> : null}
        {busy ? 'WORKING…' : reason || 'PAY ›'}
      </Box>
      <Box sx={{ ...note, gridColumn: '1 / -1', color: off ? 'rgba(142,160,178,0.75)' : 'rgba(234,246,250,0.72)', letterSpacing: '0.1em' }}>
        {sub}
      </Box>
    </Box>
  );
};

export const TournamentFeeModal: React.FC<{
  tournamentId: string;
  onPaid: () => void;
  onClose: () => void;
}> = ({ tournamentId, onPaid, onClose }) => {
  const { get, post } = useNetwork();
  const { ready, ensureReady, notReadyMessage } = useWalletReady();
  const { publicKey, signTransaction } = useWallet();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Mode | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  // onPaid is an inline arrow in the host (a new function every render): held in a ref so a parent
  // render does not refetch the fee and announce "paid" a second time (2026-11-05)
  const onPaidRef = useRef(onPaid);
  useEffect(() => { onPaidRef.current = onPaid; }, [onPaid]);
  const load = useCallback(async () => {
    try {
      const q = (await get(`/tcg/tournaments/${encodeURIComponent(tournamentId)}/fee`, true)) as Quote;
      setQuote(q);
      setError(null);
      if (q.paid) onPaidRef.current();
    } catch (e) {
      setError(words(e, 'Could not load the entry fee right now.'));
    }
  }, [get, tournamentId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const pay = async (mode: Mode) => {
    if (!quote?.fee || busy) return;
    setError(null);
    setHint(null);
    let connectedWallet: string | null = null;
    if (mode !== 'earnedTokens') {
      // the marketplace's rule: a wallet that is usable now, or a connect attempt and "then tap again"
      if (!ensureReady() || !publicKey || !signTransaction) {
        setHint(notReadyMessage);
        return;
      }
      connectedWallet = publicKey.toBase58();
    }
    setBusy(mode);
    try {
      const it = (await post(
        `/tcg/tournaments/${encodeURIComponent(tournamentId)}/fee/intent`,
        // the quote the member saw: an A$ fee re-priced more than 1% above it is refused (PRICE_MOVED)
        { mode, connectedWallet, quotedUnits: quote?.fee?.incUnits ?? null },
        true
      )) as { paid: boolean; feeId: number; transactionBase64?: string };
      if (it.paid) {
        onPaid();
        return;
      }
      // the wallet signs what the server built (as fee payer, or as the whole payer)
      const tx = Transaction.from(Buffer.from(it.transactionBase64 || '', 'base64'));
      const signed = await signTransaction!(tx);
      const signedTransactionBase64 = Buffer.from(
        signed.serialize({ requireAllSignatures: false, verifySignatures: false })
      ).toString('base64');
      await post(
        `/tcg/tournaments/${tournamentId}/fee/confirm`,
        { feeId: it.feeId, signedTransactionBase64, connectedWallet },
        true
      );
      onPaid();
    } catch (e) {
      // useNetwork throws { status, data } — the code sits on data, not on an axios-style response
      const code = (e as { data?: { code?: string } } | null)?.data?.code;
      if (code === 'PRICE_MOVED') { await load(); } // the new price is shown; nothing was charged
      setError(words(e, 'The payment did not go through. Nothing has been charged.'));
    } finally {
      setBusy(null);
    }
  };

  const fee = quote?.fee;
  const need = fee ? num(fee.inc) : NaN;
  const earned = quote ? num(quote.balances.earned) : NaN;
  const wallet = quote ? num(quote.balances.wallet) : NaN;
  const earnedShort = Number.isFinite(need) && Number.isFinite(earned) && earned < need;
  const walletShort = Number.isFinite(need) && Number.isFinite(wallet) && wallet < need;

  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-label="Tournament entry fee"
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 21,
        display: 'grid',
        placeItems: 'center',
        p: 2,
        background:
          'radial-gradient(60% 45% at 50% 42%, rgba(255,196,77,0.10), rgba(255,196,77,0) 70%), rgba(4,6,11,0.8)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        color: TEXT,
        fontFamily: SANS,
        '@keyframes tfIn': { from: { opacity: 0, transform: 'translateY(8px) scale(.98)' }, to: { opacity: 1, transform: 'none' } }
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 440,
          display: 'grid',
          gap: 1.75,
          p: '20px 20px 16px',
          borderRadius: '18px',
          border: '1.5px solid rgba(255,196,77,0.65)',
          background: 'linear-gradient(180deg, rgba(16,20,30,0.98), rgba(9,13,21,0.98))',
          boxShadow: '0 24px 60px rgba(0,0,0,0.7), 0 0 36px rgba(255,196,77,0.14)',
          animation: 'tfIn .22s ease-out',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
        }}
      >
        {/* who and what */}
        <Box sx={{ display: 'grid', gap: 0.5, textAlign: 'center' }}>
          <Box sx={eyebrow}>TOURNAMENT · ENTRY FEE</Box>
          {quote && (
            <Box sx={{ fontFamily: SANS, fontWeight: 800, fontSize: 18, letterSpacing: '0.01em', lineHeight: 1.2, overflowWrap: 'anywhere' }}>
              {quote.name}
            </Box>
          )}
        </Box>

        {/* the amount, as a plate */}
        {!quote && !error && (
          <Box sx={{ display: 'grid', justifyItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={22} thickness={5} sx={{ color: GOLD }} />
            <Box sx={{ ...eyebrow, color: DIM, letterSpacing: '0.16em' }}>GETTING THE QUOTE…</Box>
          </Box>
        )}
        {fee && (
          <Box
            sx={{
              display: 'grid',
              justifyItems: 'center',
              gap: 0.25,
              p: '12px 14px 11px',
              borderRadius: '14px',
              border: '1px solid rgba(255,196,77,0.45)',
              background: 'linear-gradient(180deg, rgba(255,196,77,0.14), rgba(255,196,77,0.04))'
            }}
          >
            <Box sx={{ fontFamily: SANS, fontWeight: 900, fontSize: 36, lineHeight: 1, letterSpacing: '0.01em', color: TEXT, fontVariantNumeric: 'tabular-nums' }}>
              {fee.kind === 'aud' ? `A$${Number(fee.amount ?? 0).toFixed(2).replace(/\.00$/, '')}` : inc(fee.inc)}
              <Box component="span" sx={{ color: GOLD, fontSize: 20, ml: 0.75, verticalAlign: '0.18em' }}>
                {fee.kind === 'aud' ? '' : 'INC'}
              </Box>
            </Box>
            <Box sx={{ ...eyebrow, color: CYAN, letterSpacing: '0.18em' }}>
              {fee.kind === 'aud' ? `${inc(fee.inc)} INC RIGHT NOW · TO ENTER` : 'TO ENTER'}
            </Box>
            {fee.kind === 'aud' && (
              <Box sx={{ ...note, textAlign: 'center', mt: 0.5 }}>THE INC AMOUNT FOLLOWS THE LIVE PRICE AND IS FIXED WHEN YOU PAY</Box>
            )}
          </Box>
        )}

        {/* the three ways to pay */}
        {fee && quote && (
          <Box sx={{ display: 'grid', gap: 1 }}>
            <PayRow
              label="Pay with earned INC"
              sub={`BALANCE · ${inc(quote.balances.earned)} INC`}
              accent="gold"
              reason={earnedShort ? 'NOT ENOUGH' : null}
              busy={busy === 'earnedTokens'}
              disabled={!!busy}
              onClick={() => pay('earnedTokens')}
            />
            <PayRow
              label="Pay from my INC wallet"
              sub={quote.balances.hasCustodyWallet ? `BALANCE · ${inc(quote.balances.wallet)} INC · ON-CHAIN` : 'YOU HAVE NO INC WALLET YET'}
              accent="cyan"
              reason={!quote.balances.hasCustodyWallet ? 'NO WALLET' : walletShort ? 'NOT ENOUGH' : null}
              busy={busy === 'custody_inc'}
              disabled={!!busy}
              onClick={() => pay('custody_inc')}
            />
            <PayRow
              label="Pay from an external wallet"
              sub={ready ? 'ON-CHAIN · YOUR CONNECTED WALLET PAYS THE NETWORK FEE' : 'ON-CHAIN · CONNECT A WALLET FIRST, THEN TAP AGAIN'}
              accent="cyan"
              busy={busy === 'external_inc'}
              disabled={!!busy}
              onClick={() => pay('external_inc')}
            />
          </Box>
        )}

        {hint && (
          <Box role="status" sx={{ ...note, color: GOLD, textAlign: 'center', letterSpacing: '0.1em' }}>
            {hint}
          </Box>
        )}
        {error && (
          <Box
            role="alert"
            sx={{ ...note, color: RED, letterSpacing: '0.06em', p: '8px 11px', borderRadius: '10px', border: '1px solid rgba(255,80,100,0.45)', background: 'rgba(255,80,100,0.1)' }}
          >
            {error}
          </Box>
        )}

        {fee && (
          <Box sx={{ ...note, textAlign: 'center' }}>
            REFUNDED AS EARNED INC ONLY IF THE TOURNAMENT IS CANCELLED · A NO-SHOW IS NOT REFUNDED
          </Box>
        )}

        <Box
          component="button"
          type="button"
          onClick={onClose}
          disabled={!!busy}
          sx={{
            justifySelf: 'center',
            minHeight: 40,
            px: 2.5,
            borderRadius: '999px',
            border: '1px solid rgba(142,160,178,0.35)',
            background: 'transparent',
            color: DIM,
            fontFamily: MONO,
            fontWeight: 800,
            fontSize: 11,
            letterSpacing: '0.16em',
            cursor: 'pointer',
            '&:hover:not(:disabled)': { color: TEXT, borderColor: 'rgba(234,246,250,0.5)' },
            '&:disabled': { opacity: 0.45, cursor: 'not-allowed' },
            '&:focus-visible': { outline: `2px solid ${CYAN}`, outlineOffset: 2 }
          }}
        >
          NOT NOW
        </Box>
      </Box>
    </Box>
  );
};

export default TournamentFeeModal;
